/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useRef, useState, useMemo } from 'react';

// Mantine
import { Box, Group, Text } from '@mantine/core';

// Styles
import classes from './Step2.module.css';

// Components
import {
	Pagination,
	AddFiltersButton,
} from '@components/atoms';
import {
	DateFilter,
	MenuFilter,
	MenuSelectFilter,
} from '@components/molecules';
import { Datatable } from '@components/organisms';

// Datatable config
import { tableSchemaBase as columns } from './step2-productions.columns';

// Apis
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import { useSites } from 'shared/api/get-sites';
import { useProductions } from '../../api/get-productions-start-mint';

// Utils
import { convertArrayToLabelValue } from '@utils/functions';

// Store
import useStore from '@store/index';

// Dayjs
import dayjs from 'dayjs';

// Types
import {
	TProject,
	TSelectOption,
	TSite,
	TSortColumn,
} from '@global-types/index';

// I18n
import { t } from 'i18next';
import { TProduction } from '@features/sourcing/productions/types';
import { DrawerProduction } from '@features/sourcing/productions/components/DrawerProduction';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const Step2 = ({
	producerId,
}: {
	producerId: string;
}) => {
	/* ********************************** Hooks ********************************* */
	const containerRef = useRef<HTMLDivElement>(null);

	const [openDrawer, setOpenDrawer] = useState(false);
	const [resetFilterSites, setResetFilterSites] =
		useState(false);
	const [queryParamsProjects, setQueryParamsProjects] =
		useState({});

	const { data: _projects } = useMeasurabilities();
	const { data: _sites } = useSites(queryParamsProjects);
	const {
		productionStartMintsPage,
		productionStartMintsPageSize,
		setProductionsStartMintsPage,
		productionStartMintsSelectedRecords,
		setProductionsStartMintsSelectedRecords,
		productionStartMintsQueryParams,
		setProductionsStartMintsQueryParams,
		productionStartMintsSortStatus,
		setProductionsStartMintsSortStatus,
		setProductionsStartMintsSortParams,
		productionStartMintsSortParams,
		setProductionsStartMintsPageSize,
		setProductionsStartMintsSelectedRow,
		productionStartMintsSelectedRow,
	} = useStore();

	const {
		data,
		isLoading,
		total: totalItems,
		totalPages,
	} = useProductions(
		productionStartMintsPage,
		productionStartMintsPageSize,
		productionStartMintsQueryParams,
		productionStartMintsSortParams
	);

	const projects: TProject[] = useMemo(
		() =>
			_projects?.filter(
				(project: TProject) =>
					project.entity.id === producerId
			),
		[producerId, _projects]
	);
	const sites: TSite[] = useMemo(
		() =>
			_sites?.filter(
				(site: TSite) =>
					site.project.id ===
					productionStartMintsQueryParams?.project?.[0]
			),
		[productionStartMintsQueryParams?.project, _sites]
	);

	/* ********************************** Helpers ********************************* */
	const handleSortParams = (_column: TSortColumn) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'production':
				sortParams = 'production';
				break;
			case 'mintStatus':
				sortParams = 'mintStatus';
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/no-unused-expressions
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setProductionsStartMintsSortParams(sortParams);
		} else {
			setProductionsStartMintsSortParams(
				'-' + sortParams
			);
		}
	};

	const handleProjectFilter = (
		projects: TSelectOption[]
	) => {
		setProductionsStartMintsSelectedRecords([]);
		setProductionsStartMintsPage(1);
		const queryParamsProjects = {
			project: projects.map(
				(_project: TSelectOption) => _project.value
			),
			site: [],
		};
		setQueryParamsProjects(queryParamsProjects);
		setProductionsStartMintsQueryParams({
			...productionStartMintsQueryParams,
			...queryParamsProjects,
		});
		setResetFilterSites(true);
	};

	const handleSiteFilter = (sites: TSelectOption[]) => {
		setProductionsStartMintsSelectedRecords([]);
		setProductionsStartMintsPage(1);
		const queryParamsSites = {
			site: sites.map(
				(_site: TSelectOption) => _site.value
			),
		};

		setProductionsStartMintsQueryParams({
			...productionStartMintsQueryParams,
			...queryParamsSites,
		});
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			{/*Drawer production */}
			<DrawerProduction
				opened={openDrawer}
				handleverify={() => {}}
				handledelete={() => {}}
				handlestartmint={() => {}}
				onClose={() => {
					setOpenDrawer(false);
				}}
				position={'right'}
				data={productionStartMintsSelectedRow}
				fromstepper
			/>
			<Box className={classes.content}>
				<Text className={classes.title}>
					{t('productions.selectProductionsToMint')}
				</Text>
				<Box
					ref={containerRef}
					className={classes.wrapperFilters}
				>
					<Group gap={'0px'}>
						<Box className={classes.containerFilters5}>
							<Box
								className={classes.containerFilterSelect}
							>
								<MenuSelectFilter
									isPersist
									value={
										productionStartMintsQueryParams.project
									}
									label="projects"
									size="lg"
									options={convertArrayToLabelValue(
										projects
									)}
									onApply={handleProjectFilter}
									placeholder={t(
										'placeholders.searchProject'
									)}
								/>
							</Box>
							<Box className={classes.spacerFilter} />
							<Box
								className={classes.containerFilterSelect}
							>
								<MenuFilter
									isPersist
									value={
										productionStartMintsQueryParams.site
									}
									reset={resetFilterSites}
									disabled={
										!productionStartMintsQueryParams?.project
									}
									handlereset={(_isReset: boolean) => {
										setResetFilterSites(_isReset);
									}}
									onApply={handleSiteFilter}
									label="sites"
									options={convertArrayToLabelValue(
										sites
									)}
									size="lg"
									placeholderSearch={t(
										'filters.placeholderSearch.selectSite'
									)}
									placeholder={t(
										'filters.placeholders.selectSite'
									)}
								/>
							</Box>
							<Box className={classes.spacerFilter} />

							<Box
								className={classes.containerFilterSelect}
							>
								<DateFilter
									disabled={
										!productionStartMintsQueryParams?.project
									}
									onApply={(date) => {
										setProductionsStartMintsSelectedRecords(
											[]
										);
										let startDate;
										let endDate;
										if (
											dayjs(date[0].value[0]).isValid() &&
											dayjs(date[0].value[1]).isValid()
										) {
											startDate = date[0].value[0];
											endDate = date[0].value[1];
										} else {
											startDate = dayjs()
												.subtract(364, 'days')
												.toISOString();
											endDate = dayjs().toISOString();
										}

										const queryParamsDate = {
											startDate: {
												$gte: startDate,
												$lt: endDate,
											},
										};

										setProductionsStartMintsQueryParams({
											...productionStartMintsQueryParams,
											...queryParamsDate,
										});
										setProductionsStartMintsPage(1);
									}}
									placeholder="Search project"
								/>
							</Box>
							<Box className={classes.spacerFilter} />
							<Box
								className={classes.containerFilterNumeric}
							>
								<AddFiltersButton>
									{t('global.addFilters')}
								</AddFiltersButton>
							</Box>
						</Box>
					</Group>
				</Box>
				<Box className={classes.contentDatatable}>
					<Datatable
						type="productionStartMint"
						loading={
							isLoading ? isLoading.toString() : undefined
						}
						columns={columns}
						data={data}
						onRowClick={({
							record,
						}: {
							record: TProduction;
						}) => {
							const IS_EXIST =
								productionStartMintsSelectedRecords.findIndex(
									(item: any) => item.id === record.id
								);

							if (IS_EXIST !== -1) {
								setProductionsStartMintsSelectedRecords(
									[
										...productionStartMintsSelectedRecords,
									].filter(
										(item) => item.id !== record.id
									)
								);
							} else {
								setProductionsStartMintsSelectedRecords([
									record,
									...productionStartMintsSelectedRecords,
								]);
							}
						}}
						onViewMore={(row: TProduction) => {
							setProductionsStartMintsSelectedRow(row);
							setOpenDrawer(true);
						}}
						onRemoveItem={() => {}}
						style={{
							minHeight: '100%',
						}}
						height={280}
						selectedRecords={
							productionStartMintsSelectedRecords
						}
						onSelectedRecordsChange={(
							selectedRecords: TProduction
						) => {
							setProductionsStartMintsSelectedRecords(
								selectedRecords
							);
						}}
						onSortStatusChange={(data: TSortColumn) => {
							handleSortParams(data);
							setProductionsStartMintsSortStatus(data);
						}}
						sortstatus={productionStartMintsSortStatus}
					/>
				</Box>
				<Pagination
					pagesize={productionStartMintsPageSize}
					totalitems={totalItems}
					activepage={productionStartMintsPage}
					handlechangesize={function (
						_size: string | number
					) {
						setProductionsStartMintsPageSize(_size);
						setProductionsStartMintsPage(1);
					}}
					onChange={(data) => {
						setProductionsStartMintsPage(data);
					}}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};
